/* eslint-disable react/no-array-index-key */
/* ========================================================= * Material Kit 2 PRO React - v2.0.0 ========================================================= * Product Page: https://www.creative-tim.com/product/material-kit-pro-react * Copyright 2021 Creative Tim (https://www.creative-tim.com) Coded by www.creative-tim.com ========================================================= * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. */

import { useEffect, useRef } from "react";
import { useMaterialUIController } from "/context";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from 'next/router'
import config from "/config";
import { robotoSlab } from "/utils/fonts";
import MDBox from "/components/MDBox";
import { findArticlePaths, findArticleByArticlePath } from '/pages/api/posts';

const Card = dynamic(() => import("@mui/material/Card"), { loading: () => null });
const Container = dynamic(() => import("@mui/material/Container"), { loading: () => null });
const Grid = dynamic(() => import("@mui/material/Grid"), { loading: () => null });
const { Link: MuiLink } = dynamic(() => import("@mui/material/Link"), { loading: () => null });
const Facebook = dynamic(() => import('@mui/icons-material/Facebook'), { loading: () => null });
const Twitter = dynamic(() => import('@mui/icons-material/Twitter'), { loading: () => null });
const YouTube = dynamic(() => import('@mui/icons-material/YouTube'), { loading: () => null });
const Instagram = dynamic(() => import('@mui/icons-material/Instagram'), { loading: () => null });
const Pinterest = dynamic(() => import('@mui/icons-material/Pinterest'), { loading: () => null });
const MDSocialButton = dynamic(() => import('/components/MDSocialButton'), { loading: () => null });
const MDTypography = dynamic(() => import('/components/MDTypography'), { loading: () => null });
const PageLayout = dynamic(() => import("/examples/PageLayout"), { loading: () => null });
const ContactUs = dynamic(() => import('/examples/ContactUs'), { loading: () => null });
const ALLDVideo = dynamic(() => import('/examples/ALLDVideo'), { loading: () => null });
const GoogleAnalytics = dynamic(() => import('/examples/GoogleAnalytics'), { loading: () => null });

function SingleArticle({ article }) {
  const [controller] = useMaterialUIController();
  const headerRef = useRef(null);
  const router = useRouter();
  const path = router.query.path || [];
  const target = path;

  useEffect(() => {
    let parallax = null;
    const start = async () => {
      if (headerRef.current !== null) {
        await import("rellax").then(u => parallax = new u.default(headerRef.current, { speed: -6 })
        )
      }
    };
    start();
    return () => {
      if (parallax !== undefined && parallax !== null) parallax.destroy()
    }
  }, [headerRef.current]);

  return (
    <>
      <Head>
        <title key="title">{`${article.title} | Autumn Leaf`}</title>
        <meta name="description" content={article.description} />
        <link rel="canonical" href={`${config.frontURL}/articles/${article.path}`} />
        <meta property="og:site_name" content="Autumn Leaf Landscape" />
        <meta property="og:url" content={`${config.frontURL}/articles/${article.path}`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.description} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content={`${config.frontURL}/articles/${article.id}/facebook.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="photo" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.description} />
        <meta name="twitter:url" content={`${config.frontURL}/articles/${article.path}`} />
        <meta name="twitter:image" content={`${config.frontURL}/articles/${article.id}/twitter.jpg`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `${config.frontURL}/articles/${article.path}`
              },
              "headline": article.title,
              "description": article.description,
              "image": `${config.frontURL}/articles/${article.bannerImage.value}`,
              "author": {
                "@type": "Organization",
                "name": "Autumn Leaf Landscape Design",
                "url": config.frontURL,
              },
              "publisher": {
                "@type": "Organization",
                "name": "Autumn Leaf Landscape Design",
                "logo": {
                  "@type": "ImageObject",
                  "url": `${config.frontURL}/logo.png`
                }
              },
              "datePublished": article.created,
              "dateModified": article.created,
              "image": {
                "@type": "ImageObject",
                "@id": `${config.frontURL}/articles/${article.bannerImage.value}`,
                "url": `${config.frontURL}/articles/${article.bannerImage.value}`,
                "height": article.bannerImageHeight,
                "width": article.bannerImageWidth
              },
              "url": `${config.frontURL}/articles/${article.path}`,
              "isPartOf": {
                "@type": "Blog",
                "@id": `${config.frontURL}/articles/`,
                "name": "Autumn Leaf Blog",
                "publisher": {
                  "@type": "Organization",
                  "@id": config.frontURL,
                  "name": "Autumn Leaf Landscape Design"
                }
              },
              "commentCount": "0",
              "about": [
                {
                  "@type": "Thing",
                  "@id": article.category,
                  "name": article.category,
                },
              ],
              "wordCount": article.wordCount,
              "keywords": [
                article.category,
              ]
            })
          }}
        />
      </Head>
      <GoogleAnalytics />
      <PageLayout relative transparent light>
        <MDBox
          ref={headerRef}
          minHeight="85vh"
          width="100%"
          sx={{
            background: "#000",
            backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
              `${linearGradient(
                rgba(gradients.dark.main, 0.8),
                rgba(gradients.dark.state, 0.8)
              )}`,
            display: "grid",
            placeItems: "center",
            position: "relative",
          }}
        >
          <Image
            src={`/articles/${article.id}/${article.bannerImage.value}`}
            alt={article.title}
            fill
            priority
            style={{ objectFit: "cover", objectPosition: "center" }}
          />
          <MDBox
            sx={{
              background: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
                `${linearGradient(
                  rgba(gradients.dark.main, 0.8),
                  rgba(gradients.dark.state, 0.8)
                )}`,
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 0,
              width: "100%",
              height: "100%",
            }}
          />
          <Container>
            <Grid container item xs={12} lg={7} justifyContent="center" flexDirection="column">
              <MDTypography
                variant="h1"
                color="white"
                sx={({ breakpoints, typography: { size } }) => ({
                  zIndex: 1,
                  display: "block",
                  minHeight: 80,
                  [breakpoints.down("md")]: {
                    fontSize: size["3xl"],
                  },
                })}
                className={robotoSlab.className}
              >
                {article.title}
              </MDTypography>
              <MDTypography
                variant="body2"
                color="white"
                sx={{ fontSize: 20, zIndex: 0 }}
              >
                {article.description}
              </MDTypography>
              <br />
              <MDTypography
                variant="body1"
                color="white"
                opacity={0.8}
                mb={2}
                mr={{ xs: 0, sm: 6 }}
                pr={{ xs: 0, sm: 6 }}
              >
                {article.created}
              </MDTypography>
              <MDTypography variant="h5" color="white" mt={2} mb={1}>
                Connect with us on
              </MDTypography>
              <MDBox display="flex" alignItems="center">
                <MDTypography
                  component="a"
                  variant="body1"
                  color="white"
                  href={config.FbURL}
                  mr={3}
                >
                  <Facebook />
                </MDTypography>
                <MDTypography
                  component="a"
                  variant="body1"
                  color="white"
                  href={config.TwitterURL}
                  mr={3}
                >
                  <Twitter />
                </MDTypography>
                <MDTypography
                  component="a"
                  variant="body1"
                  color="white"
                  href={config.InstagramURL}
                  mr={3}
                >
                  <Instagram />
                </MDTypography>
                <MDTypography
                  component="a"
                  variant="body1"
                  color="white"
                  href={config.YoutubeURL}
                >
                  <YouTube />
                </MDTypography>
              </MDBox>
            </Grid>
          </Container>
        </MDBox>
        <Card
          sx={{
            p: controller.mobileView ? 1 : 2,
            mx: { xs: 1, lg: 3 },
            mt: -8, mb: 4,
            boxShadow: ({ boxShadows: { xxl } }) => xxl
          }}>
          <MDBox component="section" py={6} mt={6}>
            <Container>
              <Grid container spacing={3}>
                <Grid
                  item
                  xs={12}
                  lg={8}
                  mx="auto"
                  style={{ flexDirection: "column" }}
                >
                  <MDTypography
                    component="h6"
                    variant="button"
                    opacity={0.7}
                    textTransform="uppercase"
                    fontWeight="bold"
                  >
                    {article.title}
                  </MDTypography>
                  <br />
                  {article.rows.map((row, rowIndex) => {
                    if (row.type === "h2") return (
                      <MDTypography variant="h2" component="h2" mb={2} key={rowIndex} className={robotoSlab.className}>
                        {row.value}
                      </MDTypography>
                    );
                    if (row.type === "p") return (
                      <MDTypography variant="body2" fontWeight="regular" component="p" mb={2} key={rowIndex}>
                        {row.value}
                      </MDTypography>
                    );
                    if (row.type === "video") return (
                      <ALLDVideo
                        key={rowIndex}
                        row={{
                          url: `/articles${row.thumbnail}`,
                          title: row.caption,
                          vid: row.vid,
                        }} />
                    );
                    if (row.type === "ol") return (
                      <ol style={{ paddingLeft: "24px", color: "#808080", fontSize: 14 }} key={`ol-${rowIndex}`}>
                        {row.value.map((line, listItemIndex) => (
                          <li key={`${rowIndex}-${listItemIndex}`}>
                            {line.map((object, lineIndex) => {
                              if (object.type === "h2") return (
                                <MDTypography variant="h2" component="h2" mb={2} key={`ol-${rowIndex}-li-${listItemIndex}-p-${lineIndex}`}>
                                  {object.value}
                                </MDTypography>
                              );
                              if (object.type === "h3") return (
                                <MDTypography variant="h3" component="h3" mb={2} key={`ol-${rowIndex}-li-${listItemIndex}-p-${lineIndex}`}>
                                  {object.value}
                                </MDTypography>
                              );
                              if (object.type === "p") return (
                                <MDTypography variant="body2" fontWeight="regular" component="div" mb={2} key={`ol-${rowIndex}-li-${listItemIndex}-p-${lineIndex}`}>
                                  {object.value}
                                </MDTypography>
                              );
                              if (object.type === "externalLink") return (
                                <MDTypography
                                  component={MuiLink}
                                  href={object.url}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  variant="body2"
                                  fontWeight="regular"
                                  mr={3}
                                  key={`ol-${rowIndex}-li-${listItemIndex}-externalLink-${lineIndex}`}
                                >
                                  {object.value}
                                </MDTypography>
                              )
                              if (object.type === "internalLink") return (
                                <Link href={object.url} key={`ol-${rowIndex}-li-${listItemIndex}-internalLink-${lineIndex}`}>
                                  <MDTypography
                                    variant="body2"
                                    mb={1}
                                    fontWeight="regular"
                                    sx={{ cursor: "pointer", display: "inline" }}
                                  >
                                    {object.value}
                                  </MDTypography>
                                </Link>
                              )
                              if (object.type === "ul") {
                                return (
                                  <ol style={{ paddingLeft: "24px", color: "#808080", fontSize: 14 }} key={`ol-${rowIndex}-li-${listItemIndex}-ul-${lineIndex}`}>
                                    {
                                      object.value.map((li, liIndex) => (
                                        <li>
                                          {li.map((UlliItem, UlliItemIndex) => {
                                            if (UlliItem.type === "h3")
                                              return (
                                                <MDTypography variant="h3" component="h3" mb={2} key={`ol-${rowIndex}-li-${listItemIndex}-ul-${lineIndex}--li-${liIndex}-h2-${UlliItemIndex}`}>
                                                  {UlliItem.value}
                                                </MDTypography>
                                              )
                                            if (UlliItem.type === "p")
                                              return (
                                                <MDTypography variant="body2" component="div" mb={2} key={`ol-${rowIndex}-li-${listItemIndex}-ul-${lineIndex}--li-${liIndex}-p-${UlliItemIndex}`}>
                                                  {UlliItem.value}
                                                </MDTypography>
                                              )
                                            return null;
                                          })
                                          }
                                        </li>
                                      ))
                                    }
                                  </ol>)
                              }

                            }
                            )}
                          </li>
                        ))}
                      </ol>
                    );
                    return null;
                  })}
                </Grid>
              </Grid>
            </Container>
          </MDBox>
          {/* <Steps /> */}
          {/* <OurEfforts />
        <Features />
        <Posts /> */}
          <MDBox pt={18} pb={6}>
            <Container>
              <Grid container spacing={3}>
                <Grid
                  item
                  xs={12}
                  lg={5}
                  ml="auto"
                  sx={{ textAlign: { xs: "center", lg: "left" } }}
                >
                  <MDTypography variant="h4" fontWeight="bold" mb={0.5}>
                    Autumn Leaf
                  </MDTypography>
                  <MDTypography variant="body1" color="text">
                    Plan. Design. Build. Enjoy for a lifetime.
                  </MDTypography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  lg={5}
                  my={{ xs: 5, lg: "auto" }}
                  mr={{ xs: 0, lg: "auto" }}
                  sx={{ textAlign: { xs: "center", lg: "right" } }}
                >
                  <MDSocialButton
                    component="a"
                    href={`https://twitter.com/intent/tweet?url=${config.frontURL}&amp;text=Check%20out%20Autumn%20Leaf`}
                    target="_blank"
                    color="twitter"
                    sx={{ mr: 1 }}
                    startIcon={<Twitter />}
                  >
                    &nbsp;Tweet
                  </MDSocialButton>
                  <MDSocialButton
                    component="a"
                    href={`https://www.facebook.com/sharer/sharer.php?u=${config.frontURL}/articles/${target}`}
                    target="_blank"
                    color="facebook"
                    sx={{ mr: 1 }}
                    startIcon={<Facebook />}
                  >
                    &nbsp;Share
                  </MDSocialButton>
                  <MDSocialButton
                    component="a"
                    href={`https://www.pinterest.com/pin/create/button/?url=${config.frontURL}`}
                    target="_blank"
                    color="pinterest"
                    startIcon={<Pinterest />}
                  >
                    &nbsp;Pin it
                  </MDSocialButton>
                </Grid>
              </Grid>
            </Container>
          </MDBox>
        </Card>
        <MDBox id="message">
          <ContactUs />
        </MDBox>
      </PageLayout>
    </>
  );
}

export default SingleArticle;


export async function getStaticPaths() {
  const posts = await findArticlePaths();
  const paths = posts.map((post) => ({
    params: { path: post.path },
  }));
  return { paths, fallback: false };
}


export async function getStaticProps({ params }) {
  const article = findArticleByArticlePath(params.path);

  return { props: { article, path: params.path } };
}
